import React, { useEffect, useState, useRef } from "react";

export const RightPanelEquations = ({ editor, isShow, isOpen }) => {
  const [equations, setEquations] = useState([]);

  useEffect(() => {
    if (editor?.dom && isShow === "equations" && isOpen) {
      const allEqs = editor.dom.select("disp-formula, inline-formula");
      const allXrefs = editor.dom.select("xref");

      if (allEqs && allEqs.length > 0) {
        const newEquations = [];
        allEqs.forEach((item, index) => {
          let id = item.getAttribute("id") || item.getAttribute("a_id") || item.getAttribute("ref") || "";
          const isInline = item.tagName.toLowerCase() === "inline-formula";
          const parentP = item.closest("p");
          
          // Extract label text
          let labelText = item.querySelector("label")?.textContent || parentP?.querySelector("label")?.textContent || "";
          
          // If inside a table, look for label cell in the same row (e.g. adjacent td containing "(1)")
          if (!labelText && item.closest("td")) {
            const row = item.closest("tr");
            if (row) {
              const cells = Array.from(row.querySelectorAll("td"));
              const cellIndex = cells.indexOf(item.closest("td"));
              cells.forEach((cell, idx) => {
                if (idx !== cellIndex) {
                  const txt = cell.textContent.trim();
                  if (/^\(\d+\)$/.test(txt) || /^\d+$/.test(txt)) {
                    labelText = txt;
                  }
                }
              });
            }
          }

          // Fallback to ID number if no label text
          const labelNum = labelText.replace(/\D/g, "");
          
          // Map ID to eqn-[num] based on the parsed label number, ignoring physical sequence index
          if (labelNum) {
            id = `eqn-${labelNum}`;
          } else if (!id) {
            id = `eqn-${index + 1}`;
          }
          
          let label = labelText || id;
          if (!label) {
            label = isInline ? "Inline Equation" : "Equation";
          }
          
          // Get actual MathML HTML or equation content
          const mathEl = item.querySelector("math, mml\\:math");
          const mathHtml = mathEl ? mathEl.outerHTML : "";
          const texMath = item.querySelector("tex-math")?.getAttribute("c-data") || item.querySelector("tex-math")?.textContent || item.textContent || "";

          const xrefCount = Array.from(allXrefs).filter((xref) => {
            const refType = xref.getAttribute("ref-type") || "";
            if (refType !== "disp-formula" && refType !== "inline-formula") return false;
            const href = xref.getAttribute("href") || "";
            const rid = xref.getAttribute("rid") || "";
            
            const cleanId = id.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
            const cleanHref = href.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
            const cleanRid = rid.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
            
            const idNum = id.replace(/\D/g, "");
            const hrefNum = href.replace(/\D/g, "");
            const ridNum = rid.replace(/\D/g, "");
            
            const matchesStr = cleanId === cleanRid || cleanId === cleanHref;
            const matchesNum = !!(idNum && (idNum === hrefNum || idNum === ridNum));
            
            return matchesStr || matchesNum;
          }).length;

          newEquations.push({
            id,
            label,
            isInline,
            mathHtml,
            formula: texMath.trim(),
            count: xrefCount,
          });
        });
        setEquations(newEquations);
      } else {
        setEquations([]);
      }
    }
  }, [isShow, isOpen, editor]);

  const selectEqInEditor = (id, isInline) => {
    if (!editor || !id) return;
    const dom = editor.dom;
    
    // Find the element in the editor DOM
    const allEqs = dom.select("disp-formula, inline-formula");
    
    // Check if any element matches our determined id or sequence
    let queryEle = allEqs.find(el => {
      const elId = el.getAttribute("id") || el.getAttribute("a_id") || el.getAttribute("ref") || "";
      if (elId === id) return true;
      
      // Fallback matching using labels/adjacent td cells
      let elLabelText = el.querySelector("label")?.textContent || el.closest("p")?.querySelector("label")?.textContent || "";
      if (!elLabelText && el.closest("td")) {
        const row = el.closest("tr");
        if (row) {
          const cells = Array.from(row.querySelectorAll("td"));
          const cellIndex = cells.indexOf(el.closest("td"));
          cells.forEach((cell, idx) => {
            if (idx !== cellIndex) {
              const txt = cell.textContent.trim();
              if (/^\(\d+\)$/.test(txt) || /^\d+$/.test(txt)) {
                elLabelText = txt;
              }
            }
          });
        }
      }
      const elLabelNum = elLabelText.replace(/\D/g, "");
      if (elLabelNum && `eqn-${elLabelNum}` === id) return true;
      return false;
    });

    if (queryEle) {
      editor.selection.select(queryEle);
      queryEle.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  const xrefIndicesRef = useRef({});

  const handleClick = (id) => {
    const editorBody = editor?.dom;
    if (!editorBody) return;

    const allXrefs = editorBody.select("xref");
    const xrefs = Array.from(allXrefs).filter((xref) => {
      const refType = xref.getAttribute("ref-type") || "";
      if (refType !== "disp-formula" && refType !== "inline-formula") return false;
      const href = xref.getAttribute("href") || "";
      const rid = xref.getAttribute("rid") || "";
      
      const cleanId = id.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
      const cleanHref = href.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
      const cleanRid = rid.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
      
      const idNum = id.replace(/\D/g, "");
      const hrefNum = href.replace(/\D/g, "");
      const ridNum = rid.replace(/\D/g, "");
      
      const matchesStr = cleanId === cleanRid || cleanId === cleanHref;
      const matchesNum = !!(idNum && (idNum === hrefNum || idNum === ridNum));
      
      return matchesStr || matchesNum;
    });

    if (!xrefs.length) return;

    if (xrefIndicesRef.current[id] === undefined) {
      xrefIndicesRef.current[id] = 0;
    }

    const currentIndex = xrefIndicesRef.current[id] % xrefs.length;
    const targetElement = xrefs[currentIndex];

    if (targetElement) {
      editor.selection.select(targetElement);
      targetElement.focus();
      targetElement.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }

    xrefIndicesRef.current[id] = (xrefIndicesRef.current[id] + 1) % xrefs.length;
  };

  if (isShow !== "equations") return null;

  return (
    <div className="w-full flex flex-col gap-4">
      {equations.length > 0 ? (
        <div className="flex flex-col gap-3">
          {equations.map((item, i) => (
            <div
              key={i}
              className="relative w-full flex flex-col cursor-pointer bg-white rounded-md hover:bg-panelrefbg border border-grey py-2 px-[6px] hover:border-panelref"
            >
              <div
                onClick={() => selectEqInEditor(item.id, item.isInline)}
                className="flex flex-col w-full gap-[6px] pb-6"
              >
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm text-[#4F46E5]">{item.label}</span>
                  {item.isInline && (
                    <span className="text-[10px] bg-[#EEF0FF] text-[#4F46E5] px-1.5 py-0.5 rounded font-medium">
                      Inline
                    </span>
                  )}
                </div>
                {item.mathHtml ? (
                  <div 
                    className="w-full bg-[#FCFBF9] p-3 rounded border border-[#E3E0D8] overflow-x-auto flex justify-center items-center mathml-container"
                    dangerouslySetInnerHTML={{ __html: item.mathHtml }}
                  />
                ) : item.formula ? (
                  <pre className="text-[12px] bg-[#FCFBF9] p-2 rounded border border-[#E3E0D8] overflow-x-auto font-mono text-gray-700 whitespace-pre-wrap break-all">
                    {item.formula}
                  </pre>
                ) : null}
              </div>
              {!item.isInline && (
                <div className="flex justify-end z-9 absolute bottom-[8px] right-[8px]">
                  <span
                    onClick={() => handleClick(item.id)}
                    className="text-[12px] text-green hover:text-red font-medium"
                  >
                    cited{`(${item.count})`}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center text-gray-400 py-8 text-sm">No equations found in document</div>
      )}
    </div>
  );
};
